import { SvgIcon, type SvgIconProps } from '@cinch-labs/design-system'
import cx from 'classnames'
import { type AnchorHTMLAttributes } from 'react'
import { useAnalytics } from '@cinch-nx/shared-analytics'
import { TrackingEventTypes } from '@cinch-nx/shared-analytics'

import styles from './button.module.css'

export type IconName =
  | 'ai-sparkles'
  | 'arrow-right'
  | 'search'
  | 'sliders'
  | 'switch'

const iconNames: Record<IconName, SvgIconProps['name']> = {
  'ai-sparkles': 'stars',
  'arrow-right': 'arrow-right',
  search: 'search',
  sliders: 'filter',
  switch: 'arrows-left-right',
}

export type ButtonProps = Omit<
  AnchorHTMLAttributes<HTMLAnchorElement>,
  'children' | 'href'
> & {
  label: string
  href: string
  icon?: IconName
  iconPosition?: 'left'
  size?: 'small'
  variant?: 'outlined'
  as?: 'a'
}

export const Button = ({
  label,
  href,
  icon,
  iconPosition: _iconPosition,
  size: _size,
  variant: _variant,
  as: _as,
  className,
  ...anchorProps
}: ButtonProps) => {
  const { trackEvent } = useAnalytics()

  const trackingEvent = {
    eventName: `AiSearchInput - cta button click`,
    type: TrackingEventTypes.ADOBE,
    data: {
      event: {
        name: 'click',
        category: 'ai-search',
        action: 'click',
        label: 'search_box',
        value: label,
      },
    },
  }

  const iconName =
    icon && Object.prototype.hasOwnProperty.call(iconNames, icon)
      ? iconNames[icon as IconName]
      : undefined

  const renderedIcon = iconName ? (
    <span
      className={cx(styles.icon, {
        [styles.starsIcon]: iconName === 'stars',
      })}
      aria-hidden="true"
    >
      <SvgIcon name={iconName} size={16} aria-hidden="true" focusable="false" />
    </span>
  ) : null

  return (
    <a
      {...anchorProps}
      className={cx(styles.button, className)}
      href={href}
      onClick={() => trackEvent(trackingEvent)}
    >
      {renderedIcon}
      {label}
    </a>
  )
}
